import { useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  Storefront, Tag, MagnifyingGlass, MapPin, Basket, Users as UsersIcon, Clock,
  CalendarBlank, CurrencyCircleDollar, Repeat, Package, ChartBar,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { Card, Loading, catColor } from "@/components/riq";
import SortableTable from "@/components/SortableTable";
import { useAdminNav } from "@/pages/adminNav";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const zar = (v) => money(v, "ZAR");

export default function AdminMerchants() {
  const { openReceipts } = useAdminNav();
  const [mode, setMode] = useState("merchants"); // merchants | brands
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null); // {name}
  const [overview, setOverview] = useState(null);
  const [ovLoading, setOvLoading] = useState(false);

  const load = (m = mode, q = search) => {
    setRows(null);
    const path = m === "merchants" ? "/admin/merchants" : "/admin/brands";
    api.get(`${path}${q ? `?search=${encodeURIComponent(q)}` : ""}`).then((r) => setRows(r.data)).catch(() => setRows(false));
  };

  useEffect(() => { load(mode, search); /* eslint-disable-next-line */ }, [mode]);

  const openItem = async (name) => {
    setSelected({ name });
    setOverview({ __loading: true });
    setOvLoading(true);
    const path = mode === "merchants" ? "/admin/merchants/overview" : "/admin/brands/overview";
    try {
      const { data } = await api.get(`${path}?name=${encodeURIComponent(name)}`);
      setOverview(data);
    } catch {
      toast.error("Could not load analytics.");
      setSelected(null);
      setOverview(null);
    } finally {
      setOvLoading(false);
    }
  };

  if (rows === false) return <div className="text-muted-foreground">Could not load merchants.</div>;

  const nameLink = (name, onAnalytics) => (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="w-8 h-8 rounded-xl bg-riq-primary/10 text-riq-primary flex items-center justify-center shrink-0">
        {mode === "merchants" ? <Storefront size={16} weight="duotone" /> : <Tag size={16} weight="duotone" />}
      </div>
      <button
        data-testid={`${mode === "merchants" ? "merchant" : "brand"}-name-link-${name}`}
        onClick={() => openReceipts(mode === "merchants" ? { merchant: name } : { brand: name })}
        className="font-semibold text-riq-primary hover:underline truncate text-left"
        title="Show receipts"
      >
        {name}
      </button>
    </div>
  );

  const analyticsBtn = (name) => (
    <button
      data-testid={`merchant-analytics-${name}`}
      onClick={(e) => { e.stopPropagation(); openItem(name); }}
      className="inline-flex items-center gap-1 rounded-full border border-riq-border px-2.5 py-1 text-xs font-medium text-neutral-600 hover:border-riq-primary"
    >
      <ChartBar size={13} weight="bold" /> Analytics
    </button>
  );

  const merchantCols = [
    { key: "name", label: "Merchant", type: "text", render: (r) => nameLink(r.name) },
    { key: "category", label: "Category", type: "text", render: (r) => <span className="text-muted-foreground truncate">{r.category || "—"}</span> },
    { key: "scans", label: "Scans", type: "number", align: "right" },
    { key: "customers", label: "Customers", type: "number", align: "right" },
    { key: "avg_basket", label: "Avg basket", type: "number", align: "right", render: (r) => zar(r.avg_basket) },
    { key: "gross", label: "Gross", type: "number", align: "right", render: (r) => <span className="font-semibold">{zar(r.gross)}</span> },
    { key: "last", label: "Last scan", type: "date", render: (r) => <span className="text-muted-foreground whitespace-nowrap">{r.last ? String(r.last).slice(0, 10) : "—"}</span> },
    { key: "actions", label: "", sortable: false, align: "right", render: (r) => analyticsBtn(r.name) },
  ];
  const brandCols = [
    { key: "name", label: "Brand", type: "text", render: (r) => nameLink(r.name) },
    { key: "merchants", label: "Stores", type: "number", align: "right" },
    { key: "units", label: "Units", type: "number", align: "right" },
    { key: "customers", label: "Customers", type: "number", align: "right" },
    { key: "gross", label: "Gross", type: "number", align: "right", render: (r) => <span className="font-semibold">{zar(r.gross)}</span> },
    { key: "actions", label: "", sortable: false, align: "right", render: (r) => analyticsBtn(r.name) },
  ];

  return (
    <div className="space-y-5" data-testid="admin-merchants">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="inline-flex rounded-full border border-riq-border p-1 bg-white">
          <button data-testid="merchants-mode-merchants" onClick={() => setMode("merchants")}
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${mode === "merchants" ? "bg-riq-primary text-white" : "text-neutral-600"}`}>
            <Storefront size={15} weight="bold" /> Merchants
          </button>
          <button data-testid="merchants-mode-brands" onClick={() => setMode("brands")}
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${mode === "brands" ? "bg-riq-primary text-white" : "text-neutral-600"}`}>
            <Tag size={15} weight="bold" /> Brands
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(mode, search.trim()); }} className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input data-testid="merchants-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${mode}…`} className="riq-input !pl-10 w-full" />
        </form>
      </div>

      {!rows ? <Loading /> : (
        <Card className="!p-0 overflow-hidden">
          <SortableTable
            columns={mode === "merchants" ? merchantCols : brandCols}
            rows={rows}
            defaultSort={mode === "merchants" ? [{ key: "last", dir: "desc" }, { key: "gross", dir: "desc" }] : [{ key: "gross", dir: "desc" }]}
            rowKey="name"
            testIdPrefix="merchant-row"
            empty={`No ${mode} found.`}
          />
        </Card>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && (setSelected(null), setOverview(null))}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="merchant-overview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mode === "merchants" ? <Storefront size={22} weight="duotone" className="text-riq-primary" /> : <Tag size={22} weight="duotone" className="text-riq-primary" />}
              {selected?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">{mode} analytics</DialogDescription>
          </DialogHeader>
          {ovLoading || overview?.__loading ? <div className="py-10"><Loading /></div>
            : overview?.configured ? (mode === "merchants" ? <MerchantView d={overview} /> : <BrandView d={overview} />)
              : <div className="py-8 text-center text-sm text-muted-foreground">No analytics available.</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MerchantView({ d }) {
  const { openReceipts } = useAdminNav();
  const k = d.kpis;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Mini icon={CurrencyCircleDollar} label="Gross" value={zar(k.gross)} />
        <Mini icon={Basket} label="Scans" value={k.scans} />
        <Mini icon={UsersIcon} label="Customers" value={k.customers} />
        <Mini icon={Repeat} label="Repeat" value={k.repeat_customers} />
        <Mini icon={CurrencyCircleDollar} label="Avg basket" value={zar(k.avg_basket)} />
      </div>
      <MonthlyChart data={d.monthly} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ListBlock title="Top items" icon={Package} rows={d.top_items.map((i) => ({ label: i.name, gross: i.gross, count: i.qty }))} onRow={(r) => openReceipts({ product: r.label })} />
        <ListBlock title="Categories" icon={Basket} rows={d.top_categories} colorByLabel />
      </div>
      <ListBlock title="Branches / locations" icon={MapPin} rows={d.locations} onRow={(r) => openReceipts({ location: r.label })} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <MiniChart title="Busiest hours" icon={Clock} data={d.by_hour} xKey="hour" yKey="count" fmtX={(h) => `${h}h`} />
        <MiniChart title="By weekday" icon={CalendarBlank} data={d.by_weekday} xKey="weekday" yKey="gross" money />
      </div>
    </div>
  );
}

function BrandView({ d }) {
  const { openReceipts } = useAdminNav();
  const k = d.kpis;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Mini icon={CurrencyCircleDollar} label="Gross" value={zar(k.gross)} />
        <Mini icon={Package} label="Units" value={k.units} />
        <Mini icon={Basket} label="Receipts" value={k.receipts} />
        <Mini icon={Storefront} label="Stores" value={k.merchants} />
        <Mini icon={UsersIcon} label="Customers" value={k.customers} />
        <Mini icon={CurrencyCircleDollar} label="Avg price" value={zar(k.avg_price)} />
      </div>
      <MonthlyChart data={d.monthly} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ListBlock title="Top products" icon={Package} rows={d.top_products.map((i) => ({ label: i.name, gross: i.gross, count: i.units }))} onRow={(r) => openReceipts({ product: r.label })} />
        <ListBlock title="Sold at stores" icon={Storefront} rows={d.by_merchant.map((i) => ({ label: i.label, gross: i.gross, count: i.units }))} onRow={(r) => openReceipts({ merchant: r.label })} />
      </div>
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-secondary/50 p-3">
      <Icon size={15} weight="duotone" className="text-riq-primary mb-1" />
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-head text-base font-bold text-riq-primary mt-0.5 truncate">{value}</div>
    </div>
  );
}

function MonthlyChart({ data }) {
  if (!data?.length) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-1">Monthly gross</div>
      <ResponsiveContainer width="100%" height={150}>
        <AreaChart data={data} margin={{ left: -18, right: 4, top: 4, bottom: 0 }}>
          <defs>
            <linearGradient id="mv-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6E3FFC" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#6E3FFC" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `R${Math.round(v / 1000)}k`} />
          <Tooltip formatter={(v) => zar(v)} />
          <Area type="monotone" dataKey="gross" stroke="#6E3FFC" strokeWidth={2} fill="url(#mv-grad)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function MiniChart({ title, icon: Icon, data, xKey, yKey, money: isMoney, fmtX }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1.5"><Icon size={14} weight="duotone" /> {title}</div>
      <ResponsiveContainer width="100%" height={150}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
          <XAxis dataKey={xKey} tick={{ fontSize: 9 }} interval={isMoney ? 0 : 1} tickFormatter={fmtX} />
          <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => (isMoney ? `R${Math.round(v / 1000)}k` : v)} allowDecimals={false} />
          <Tooltip formatter={(v) => (isMoney ? zar(v) : v)} />
          <Bar dataKey={yKey} fill="#6E3FFC" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ListBlock({ title, icon: Icon, rows, colorByLabel, onRow }) {
  if (!rows?.length) return null;
  const max = Math.max(1, ...rows.map((r) => r.gross));
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><Icon size={14} weight="duotone" /> {title}</div>
      <div className="space-y-2">
        {rows.slice(0, 8).map((r, i) => (
          <div key={i} className={onRow ? "cursor-pointer group" : ""} onClick={onRow ? () => onRow(r) : undefined}>
            <div className="flex items-center justify-between text-sm">
              <span className={`truncate flex-1 font-medium ${onRow ? "group-hover:text-riq-primary group-hover:underline" : ""}`}>{r.label}</span>
              <span className="text-muted-foreground text-xs mx-2">{r.count}</span>
              <span className="font-semibold w-24 text-right">{zar(r.gross)}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(r.gross / max) * 100}%`, background: colorByLabel ? catColor(r.label) : "#6E3FFC" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
